import BaseApiClient from './BaseApiClient';
import type { ListDataParams, ListDataResponse } from './types';

export const SAVINGS_API_VERSION_HEADERS = { Version: '2026-01-20' };

interface SavingsPaginatedEnvelope<T> {
  data: T[];
  meta: {
    current_page: number;
    per_page: number;
    total: number;
    last_page?: number;
  };
}

export const toSavingsListResponse = <T>(
  envelope: SavingsPaginatedEnvelope<T>
): ListDataResponse<T> => ({
  items: envelope.data,
  pagination: {
    page: envelope.meta.current_page,
    perPage: envelope.meta.per_page,
    total: envelope.meta.total,
    lastPage: envelope.meta.last_page
  }
});

/**
 * List/create/update/delete for a Savings reference resource. Subclasses name the endpoint and the
 * filter that the table's search box maps onto.
 */
export abstract class SavingsReferenceResourceClient<
  TRecord,
  TPayload extends object
> extends BaseApiClient {
  protected abstract readonly path: string;
  protected abstract readonly searchFilter: string;

  async list(
    listData: ListDataParams,
    searchTerm?: string
  ): Promise<ListDataResponse<TRecord>> {
    const filters = { ...listData.filters };
    if (searchTerm) {
      filters[this.searchFilter] = searchTerm;
    }

    const res = await this.client.getList(
      this.path,
      { ...listData, filters },
      { headers: SAVINGS_API_VERSION_HEADERS }
    );

    return toSavingsListResponse<TRecord>(res.data);
  }

  async create(payload: TPayload): Promise<TRecord> {
    const res = await this.client.post(this.path, payload, {
      headers: SAVINGS_API_VERSION_HEADERS
    });
    return res.data.data;
  }

  async update(id: number, payload: Partial<TPayload>): Promise<TRecord> {
    const res = await this.client.patch(`${this.path}/${id}`, payload, {
      headers: SAVINGS_API_VERSION_HEADERS
    });
    return res.data.data;
  }

  async delete(id: number): Promise<void> {
    await this.client.delete(`${this.path}/${id}`, {
      headers: SAVINGS_API_VERSION_HEADERS
    });
  }
}
